import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  getPlanningWorkItem,
  listPlanningObjectives,
  listPlanningWorkItems,
  PlanningCycle,
  PlanningObjective,
  PlanningSprint,
  PlanningTask,
  PlanningTaskUpsertPayload,
  PlanningWorkItem,
} from '../../services/strategicPlanningApi';
import { fetchMyTasksScope } from '../../services/myTasksApi';
import { formatPlanningObjectiveKind } from '../../types/PlanningObjectiveKind';
import { PLANNING_BACKLOG_ID, sortSprintsFromCurrentToLast } from '../../types/PlanningSprint';
import { canEditPlanningTaskOwner } from '../../types/PlanningTaskStatus';
import { CollaboratorAutocomplete } from '../ui/CollaboratorAutocomplete';
import { DraggableModalPanel } from '../ui/DraggableModalPanel';
import { RequiredMark } from '../ui/RequiredMark';
import { TaskAreaPicker } from './TaskAreaPicker';

type PlanningTaskFormModalProps = {
  open: boolean;
  cycles: PlanningCycle[];
  sprints?: PlanningSprint[];
  initial?: PlanningTask | null;
  defaultCycleId?: string;
  defaultSprintId?: string;
  defaultObjectiveId?: string;
  defaultParentWorkItemId?: string;
  defaultPlannedStartDate?: string;
  defaultPlannedEndDate?: string;
  defaultArea?: string;
  onClose: () => void;
  onSubmit: (payload: PlanningTaskUpsertPayload) => Promise<void>;
};

type TaskHierarchySelection = {
  cycleId?: string;
  objectiveId?: string;
  metaId: string;
  marcoId: string;
  /** Data inicial do marco (quando o pai é um marco). */
  plannedStartDate?: string;
};

const PRIORITIES = [
  { value: 'Low', label: 'Baixa' },
  { value: 'Medium', label: 'Média' },
  { value: 'High', label: 'Alta' },
  { value: 'Critical', label: 'Crítica' },
];

async function resolveTaskHierarchyFromParent(parentId: string): Promise<TaskHierarchySelection> {
  let current = await getPlanningWorkItem(parentId);

  for (let depth = 0; depth < 6; depth += 1) {
    if (current.kind === 'Marco') {
      const metaId = current.parentWorkItemId ?? '';
      let cycleId = current.cycleId ?? undefined;
      let objectiveId = current.objectiveId ?? undefined;

      if (metaId) {
        const meta = await getPlanningWorkItem(metaId);
        cycleId = meta.cycleId ?? cycleId;
        objectiveId = meta.objectiveId ?? objectiveId;
      }

      return {
        cycleId,
        objectiveId,
        metaId,
        marcoId: current.id,
        plannedStartDate: current.plannedStartDate ?? undefined,
      };
    }

    if (current.kind === 'Meta') {
      return {
        cycleId: current.cycleId ?? undefined,
        objectiveId: current.objectiveId ?? undefined,
        metaId: current.id,
        marcoId: '',
        plannedStartDate: current.plannedStartDate ?? undefined,
      };
    }

    if (!current.parentWorkItemId) {
      return {
        cycleId: current.cycleId ?? undefined,
        objectiveId: current.objectiveId ?? undefined,
        metaId: '',
        marcoId: '',
      };
    }

    current = await getPlanningWorkItem(current.parentWorkItemId);
  }

  return { metaId: '', marcoId: '' };
}

function isMetaOfObjective(item: PlanningWorkItem, objectiveId: string): boolean {
  return item.kind === 'Meta' && !item.parentWorkItemId && item.objectiveId === objectiveId;
}

function isMarcoOfMeta(
  item: PlanningWorkItem,
  metaId: string,
  objectiveId: string,
): boolean {
  return item.kind === 'Marco'
    && item.parentWorkItemId === metaId
    && (!item.objectiveId || item.objectiveId === objectiveId);
}

export function PlanningTaskFormModal({
  open,
  cycles,
  sprints = [],
  initial,
  defaultCycleId,
  defaultSprintId,
  defaultObjectiveId,
  defaultParentWorkItemId,
  defaultPlannedStartDate,
  defaultPlannedEndDate,
  defaultArea,
  onClose,
  onSubmit,
}: PlanningTaskFormModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [plannedStartDate, setPlannedStartDate] = useState('');
  const [plannedEndDate, setPlannedEndDate] = useState('');
  const [cycleId, setCycleId] = useState('');
  const [sprintId, setSprintId] = useState('');
  const [selectedObjectiveId, setSelectedObjectiveId] = useState('');
  const [selectedMetaId, setSelectedMetaId] = useState('');
  const [selectedMarcoId, setSelectedMarcoId] = useState('');
  const [objectives, setObjectives] = useState<PlanningObjective[]>([]);
  const [metas, setMetas] = useState<PlanningWorkItem[]>([]);
  const [marcos, setMarcos] = useState<PlanningWorkItem[]>([]);
  const [ownerUserId, setOwnerUserId] = useState('');
  const [ownerUserName, setOwnerUserName] = useState('');
  const [areas, setAreas] = useState<string[]>([]);
  const [formError, setFormError] = useState('');
  const [hierarchyReady, setHierarchyReady] = useState(true);
  const [saving, setSaving] = useState(false);
  const canChangeOwner = !initial
    || canEditPlanningTaskOwner(initial.status, initial.executionFinishedAt);
  const orderedSprints = useMemo(() => sortSprintsFromCurrentToLast(sprints), [sprints]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const parentId = initial?.parentWorkItemId ?? defaultParentWorkItemId;

    setTitle(initial?.title ?? '');
    setDescription(initial?.description ?? '');
    setPriority(initial?.priority ?? 'Medium');
    setPlannedStartDate(initial?.plannedStartDate ?? defaultPlannedStartDate ?? '');
    setPlannedEndDate(initial?.plannedEndDate ?? defaultPlannedEndDate ?? '');
    setCycleId(initial?.cycleId ?? defaultCycleId ?? '');
    setSprintId(initial?.sprintId ?? (defaultSprintId === PLANNING_BACKLOG_ID ? '' : defaultSprintId ?? ''));
    setSelectedObjectiveId(initial?.objectiveId ?? defaultObjectiveId ?? '');
    setSelectedMetaId('');
    setSelectedMarcoId('');
    setOwnerUserId(initial?.ownerUserId ?? '');
    setOwnerUserName(initial?.ownerUserName ?? '');
    setAreas(initial?.areas?.length ? initial.areas : (!initial && defaultArea ? [defaultArea] : []));
    setFormError('');
    setHierarchyReady(!parentId);

    if (!parentId) {
      return;
    }

    void (async () => {
      try {
        const resolved = await resolveTaskHierarchyFromParent(parentId);
        if (cancelled) return;

        if (resolved.cycleId) setCycleId(resolved.cycleId);
        if (resolved.objectiveId) setSelectedObjectiveId(resolved.objectiveId);
        setSelectedMetaId(resolved.metaId);
        setSelectedMarcoId(resolved.marcoId);
        // Nova tarefa: data inicial acompanha o marco (ou a meta, se não houver marco).
        if (!initial && resolved.marcoId && resolved.plannedStartDate) {
          setPlannedStartDate(resolved.plannedStartDate);
        }
      } catch {
        if (!cancelled) {
          setFormError('Não foi possível carregar o vínculo atual da tarefa.');
        }
      } finally {
        if (!cancelled) setHierarchyReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    initial,
    defaultCycleId,
    defaultSprintId,
    defaultObjectiveId,
    defaultParentWorkItemId,
    defaultPlannedStartDate,
    defaultPlannedEndDate,
    defaultArea,
  ]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    async function loadObjectives(): Promise<void> {
      try {
        const items = await listPlanningObjectives(cycleId || undefined);
        if (!cancelled) {
          setObjectives(items);
        }
      } catch {
        if (!cancelled) {
          setObjectives([]);
        }
      }
    }

    void loadObjectives();
    return () => {
      cancelled = true;
    };
  }, [open, cycleId]);

  useEffect(() => {
    if (!open || !selectedObjectiveId) {
      setMetas([]);
      return;
    }

    let cancelled = false;

    void listPlanningWorkItems({ kind: 'Meta', objectiveId: selectedObjectiveId })
      .then((items) => {
        if (cancelled) return;

        const filtered = items.filter((item) => isMetaOfObjective(item, selectedObjectiveId));
        setMetas(filtered);
        setSelectedMetaId((current) => {
          if (current && filtered.some((item) => item.id === current)) {
            return current;
          }

          setSelectedMarcoId('');
          return '';
        });
      })
      .catch(() => {
        if (!cancelled) {
          setMetas([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, selectedObjectiveId]);

  useEffect(() => {
    if (!open || !selectedMetaId) {
      setMarcos([]);
      return;
    }

    let cancelled = false;
    setMarcos([]);

    void listPlanningWorkItems({
      kind: 'Marco',
      objectiveId: selectedObjectiveId,
      parentWorkItemId: selectedMetaId,
    })
      .then((items) => {
        if (cancelled) return;

        const filtered = items.filter((item) =>
          isMarcoOfMeta(item, selectedMetaId, selectedObjectiveId));
        setMarcos(filtered);
        setSelectedMarcoId((current) =>
          current && filtered.some((item) => item.id === current) ? current : '');
      })
      .catch(() => {
        if (!cancelled) {
          setMarcos([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, selectedMetaId, selectedObjectiveId]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    void fetchMyTasksScope()
      .then((scope) => {
        if (cancelled) return;
        setOwnerUserId((current) => {
          if (current) return current;
          return scope.defaultUsers.find((user) => user.isSelf)?.userId ?? '';
        });
        setOwnerUserName((current) => {
          if (current) return current;
          return scope.defaultUsers.find((user) => user.isSelf)?.name ?? '';
        });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [open]);

  function applyCycle(nextCycleId: string): void {
    setCycleId(nextCycleId);
    setSelectedObjectiveId('');
    setSelectedMetaId('');
    setSelectedMarcoId('');
  }

  function applyObjective(nextObjectiveId: string): void {
    setSelectedObjectiveId(nextObjectiveId);
    setSelectedMetaId('');
    setSelectedMarcoId('');

    const objective = objectives.find((item) => item.id === nextObjectiveId);
    if (objective?.cycleId) {
      setCycleId(objective.cycleId);
    }
  }

  function applyMeta(nextMetaId: string): void {
    setSelectedMetaId(nextMetaId);
    setSelectedMarcoId('');
    setMarcos([]);
  }

  function applyMarco(nextMarcoId: string): void {
    const marco = marcos.find((item) => item.id === nextMarcoId);
    if (nextMarcoId && !marco) return;

    setSelectedMarcoId(nextMarcoId);
    // Ao selecionar um marco na criação, a data inicial da tarefa herda a do marco.
    if (!initial && marco?.plannedStartDate) {
      setPlannedStartDate(marco.plannedStartDate);
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError('');

    if (!selectedObjectiveId) {
      setFormError('Selecione o objetivo da tarefa.');
      return;
    }

    if (!ownerUserId) {
      setFormError('Selecione o responsável da tarefa.');
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        kind: 'Tarefa',
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        plannedStartDate: plannedStartDate || undefined,
        plannedEndDate: plannedEndDate || undefined,
        cycleId: cycleId || undefined,
        sprintId: sprintId || undefined,
        objectiveId: selectedObjectiveId,
        parentWorkItemId: selectedMarcoId || selectedMetaId || undefined,
        ownerUserId: canChangeOwner ? ownerUserId : undefined,
        tags: initial?.tags ?? undefined,
        areas,
      });
      onClose();
    } catch {
      // Mantém o modal aberto; o toast de erro vem do caller.
    } finally {
      setSaving(false);
    }
  }

  return (
    <DraggableModalPanel
      open={open}
      title={initial ? 'Editar tarefa' : 'Nova tarefa'}
      subtitle="Vincule a tarefa a um objetivo, meta e, se quiser, a um marco. Arraste a janela pela barra de título."
      onClose={onClose}
      closeDisabled={saving}
      className="w-full max-w-xl"
      overlayClassName="z-[70]"
      footer={
        <div className="erp-btn-actions">
          <button type="button" className="erp-btn-secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button
            type="submit"
            form="planning-task-form"
            className="erp-btn-primary"
            disabled={saving || !hierarchyReady}
          >
            {saving ? 'Salvando…' : initial ? 'Salvar' : 'Criar tarefa'}
          </button>
        </div>
      }
    >
      <form
        id="planning-task-form"
        className="space-y-4"
        onSubmit={(event) => void handleSubmit(event)}
      >
        {formError ? (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {formError}
          </p>
        ) : null}

        <label className="block">
          <span className="erp-label">Título</span>
          <input
            className="erp-input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            required
            maxLength={300}
          />
        </label>

        <label className="block">
          <span className="erp-label">Descrição</span>
          <textarea
            className="erp-input min-h-[80px]"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={4000}
          />
        </label>

        <label className="block">
          <span className="erp-label">
            Responsável <RequiredMark />
          </span>
          {canChangeOwner ? (
            <p className="mb-2 text-xs text-secondary-500">
              Clique para buscar. A escolha é aplicada ao sair do campo.
            </p>
          ) : (
            <p className="mb-2 text-xs text-secondary-500">
              Tarefa concluída ou cancelada: o responsável não pode ser alterado.
            </p>
          )}
          <CollaboratorAutocomplete
            valueUserId={ownerUserId || null}
            valueUserName={ownerUserName || null}
            disabled={saving || !canChangeOwner}
            onCommit={(user) => {
              setOwnerUserId(user?.userId ?? '');
              setOwnerUserName(user?.name ?? '');
            }}
          />
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="erp-label">Prioridade</span>
            <select
              className="erp-input"
              value={priority}
              onChange={(event) => setPriority(event.target.value)}
            >
              {PRIORITIES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="erp-label">Ciclo</span>
            <select
              className="erp-input"
              value={cycleId}
              onChange={(event) => applyCycle(event.target.value)}
            >
              <option value="">Selecione o ciclo</option>
              {cycles.map((cycle) => (
                <option key={cycle.id} value={cycle.id}>
                  {cycle.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="erp-label">Sprint</span>
            <select
              className="erp-input"
              value={sprintId}
              onChange={(event) => setSprintId(event.target.value)}
            >
              <option value="">Backlog (sem sprint)</option>
              {orderedSprints.map((sprint) => (
                <option key={sprint.id} value={sprint.id}>
                  {sprint.title}
                </option>
              ))}
            </select>
          </label>
        </div>

        <TaskAreaPicker
          selectedIds={areas}
          disabled={saving}
          onChange={setAreas}
        />

        <label className="block">
          <span className="erp-label">Objetivo <RequiredMark /></span>
          <select
            className="erp-input"
            value={selectedObjectiveId}
            onChange={(event) => applyObjective(event.target.value)}
            required
            disabled={!hierarchyReady}
          >
            <option value="">Selecione o objetivo</option>
            {objectives.map((objective) => (
              <option key={objective.id} value={objective.id}>
                {objective.title} ({formatPlanningObjectiveKind(objective.kind)})
              </option>
            ))}
          </select>
          {objectives.length === 0 ? (
            <p className="mt-1 text-xs text-secondary-500">
              Não há objetivos no ciclo selecionado. Cadastre um objetivo antes.
            </p>
          ) : null}
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="erp-label">Meta</span>
            <select
              className="erp-input"
              value={selectedMetaId}
              onChange={(event) => applyMeta(event.target.value)}
              disabled={!selectedObjectiveId || !hierarchyReady}
            >
              <option value="">Sem meta</option>
              {metas.map((meta) => (
                <option key={meta.id} value={meta.id}>
                  {meta.title}
                </option>
              ))}
            </select>
            {selectedObjectiveId && metas.length === 0 ? (
              <p className="mt-1 text-xs text-secondary-500">
                Não há metas neste objetivo. A tarefa ficará só no objetivo até você criar uma meta.
              </p>
            ) : (
              <p className="mt-1 text-xs text-secondary-500">
                Opcional. Use para vincular a tarefa à meta do objetivo.
              </p>
            )}
          </label>

          <label className="block">
            <span className="erp-label">Marco</span>
            <select
              className="erp-input"
              value={selectedMarcoId}
              onChange={(event) => applyMarco(event.target.value)}
              disabled={!selectedMetaId || !hierarchyReady}
            >
              <option value="">Sem marco</option>
              {marcos.map((marco) => (
                <option key={marco.id} value={marco.id}>
                  {marco.title}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-secondary-500">
              Opcional. Sem marco, a tarefa permanece na meta.
            </p>
          </label>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="erp-label">Início planejado</span>
            <input
              type="date"
              className="erp-input"
              value={plannedStartDate}
              onChange={(event) => setPlannedStartDate(event.target.value)}
            />
          </label>

          <label className="block">
            <span className="erp-label">Fim planejado</span>
            <input
              type="date"
              className="erp-input"
              value={plannedEndDate}
              onChange={(event) => setPlannedEndDate(event.target.value)}
              readOnly={Boolean(initial?.executionStartedAt)}
              disabled={Boolean(initial?.executionStartedAt)}
              title={
                initial?.executionStartedAt
                  ? 'Após iniciar a execução, use Estender prazo'
                  : undefined
              }
            />
            {initial?.executionStartedAt ? (
              <span className="mt-1 block text-xs text-secondary-500">
                Após iniciar a execução, use a ação Estender prazo.
              </span>
            ) : null}
          </label>
        </div>
      </form>
    </DraggableModalPanel>
  );
}
